/**
 * TouchTooltip.jsx — Iter 67 Phase 6.
 *
 * Tap-and-hold tooltip for iPad. Native HTML `title="..."` is
 * hover-only and useless on touch. This component shows a tooltip
 * after ~600 ms of finger-down (long-press) and dismisses on
 * touch-end / click-elsewhere.
 *
 * Usage:
 *   <TouchTooltip label="Measure distance (M)">
 *     <button>...</button>
 *   </TouchTooltip>
 *
 * Children must render exactly one element that accepts mouse + touch
 * handlers (button, anchor, div). The component clones the child to
 * attach handlers without breaking the existing event chain.
 */
import React, { useEffect, useRef, useState } from "react";

const SHOW_DELAY_MS = 600;

export default function TouchTooltip({ label, children, side = "bottom" }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const targetRef = useRef(null);
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  const computeCoords = () => {
    const el = targetRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.bottom + 6 };
  };

  const start = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setCoords(computeCoords());
      setOpen(true);
    }, SHOW_DELAY_MS);
  };
  const stop = () => {
    clearTimeout(timer.current);
    setOpen(false);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!label) return children;
  const child = React.Children.only(children);

  const cloned = React.cloneElement(child, {
    ref: (node) => {
      targetRef.current = node;
      // Preserve any forwarded ref the child already had.
      const orig = child.ref;
      if (typeof orig === "function") orig(node);
      else if (orig && typeof orig === "object") orig.current = node;
    },
    onTouchStart: (e) => {
      start();
      child.props.onTouchStart && child.props.onTouchStart(e);
    },
    onTouchEnd: (e) => {
      stop();
      child.props.onTouchEnd && child.props.onTouchEnd(e);
    },
    onTouchCancel: (e) => {
      stop();
      child.props.onTouchCancel && child.props.onTouchCancel(e);
    },
    // Desktop hover support stays working through native `title=` on the
    // wrapped element; the touch path is what was missing.
  });

  return (
    <>
      {cloned}
      {open && (
        <div
          role="tooltip"
          data-testid="touch-tooltip"
          className="fixed z-[100] px-2.5 py-1.5 rounded-md bg-[var(--fop-surface-3)] border border-[var(--fop-border-2)] text-[11px] text-[var(--fop-text)] font-mono uppercase tracking-wider shadow-xl pointer-events-none"
          style={{
            left: coords.x,
            top: coords.y,
            transform: "translate(-50%, 0)",
          }}
        >
          {label}
        </div>
      )}
    </>
  );
}
